import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Slide,
  Switch,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react"
import { starIcons } from "@components/Product/Ratings"
import TurboBadge from "@components/Product/TurboBadge"
import FilterList from "@components/Search/Filters/FilterList"
import FilterTab from "@components/Search/Filters/FilterTab"
import NewReleasesFilter, {
  newReleasesOptions,
} from "@components/Search/Filters/NewReleasesFilter"
import PriceFilter from "@components/Search/Filters/PriceFilter"
import RatingsFilter from "@components/Search/Filters/RatingsFilter"
import SortByFilter, {
  sortOptions,
} from "@components/Search/Filters/SortByFilter"
import VirtualHierarchicalMenu from "@components/Search/Filters/VirtualHierarchicalMenu"
import VirtualNumericMenu from "@components/Search/Filters/VirtualNumericMenu"
import VirtualRangeInput from "@components/Search/Filters/VirtualRangeInput"
import VirtualRefinementList from "@components/Search/Filters/VirtualRefinementList"
import VirtualSortBy from "@components/Search/Filters/VirtualSortBy"
import HierarchicalMenu, {
  menuAttributes,
} from "@components/Search/HierarchicalMenu"
import { useSortBy } from "@hooks/search/useSortBy"
import { Fragment, useEffect, useMemo, useRef, useState } from "react"
import { BiCaretDown } from "react-icons/bi"
import { IoMdClose } from "react-icons/io"
import { VscSettings } from "react-icons/vsc"
import {
  useClearRefinements,
  useCurrentRefinements,
  useDynamicWidgets,
  useNumericMenu,
} from "react-instantsearch-core"

type FilterProps = {
  title: string
  attribute: string
  component?: JSX.Element
}

type SearchFiltersProps = {
  isHome?: boolean
}
const SearchFilters = ({ isHome }: SearchFiltersProps) => {
  // useDisclosure for filter slide
  const { isOpen, onOpen, onClose } = useDisclosure()

  // useEffect when isOpen to disable body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = "unset"
    }
  }, [isOpen])

  const [tabIndex, setTabIndex] = useState<number>(0)
  const [tabName, setTabName] = useState<string>("")

  const { items: refinedItems, refine } = useCurrentRefinements()

  const filterList: FilterProps[] = useMemo(
    () => [
      {
        title: "Sort By",
        attribute: "sortBy",
        component: <SortByFilter />,
      },
      {
        title: "Categories",
        attribute: "category.lvl0.en_AE",
        component: <HierarchicalMenu />,
      },
      {
        title: "New Releases",
        attribute: "datePublished",
        component: <NewReleasesFilter />,
      },
      {
        title: "Ratings",
        attribute: "aggregateRating.ratingValue",
        component: <RatingsFilter />,
      },
      {
        title: "Price",
        attribute: "offers.lowPrice",
        component: <PriceFilter />,
      },
      {
        title: "Author",
        attribute: "author.en_AE",
      },
      {
        title: "Illustrator",
        attribute: "illustrator.en_AE",
      },
      {
        title: "Publisher",
        attribute: "publisher.en_AE",
      },
      {
        title: "Format",
        attribute: "bookFormat.en_AE",
      },
      {
        title: "Edition",
        attribute: "bookEdition.en_AE",
      },
      {
        title: "Series",
        attribute: "isPartOf.en_AE",
      },
      {
        title: "Audience",
        attribute: "audience.audienceType.en_AE",
      },
      {
        title: "Language",
        attribute: "inLanguage",
      },
      {
        title: "Accessibility",
        attribute: "accessibilityFeature",
      },
      {
        title: "Abridged",
        attribute: "abridged",
      },
      {
        title: "Availability",
        attribute: "offers.availability",
      },
    ],
    []
  )

  const { attributesToRender } = useDynamicWidgets({ maxValuesPerFacet: 100 })

  const dynamicFilterList = useMemo(
    () =>
      filterList.filter(
        (filter) =>
          attributesToRender.includes(filter.attribute) || filter.component
      ),
    [attributesToRender, filterList]
  )

  const { items: newReleasesItems } = useNumericMenu({
    attribute: "datePublished",
    items: newReleasesOptions(),
  })

  const refinedCategory = useMemo(
    () =>
      refinedItems.filter((item) => item.attribute === "category.lvl0.en_AE"),
    [refinedItems]
  )

  const refinedDatePublished = useMemo(
    () => refinedItems.filter((item) => item.attribute === "datePublished"),
    [refinedItems]
  )

  const refinedRatings = useMemo(
    () =>
      refinedItems.filter(
        (item) => item.attribute === "aggregateRating.ratingValue"
      ),
    [refinedItems]
  )

  const refinedPrice = useMemo(
    () => refinedItems.filter((item) => item.attribute === "offers.lowPrice"),
    [refinedItems]
  )

  const otherRefinements = useMemo(
    () =>
      refinedItems.filter(
        (item) =>
          item.attribute !== "category.lvl0.en_AE" &&
          item.attribute !== "datePublished" &&
          item.attribute !== "aggregateRating.ratingValue" &&
          item.attribute !== "offers.lowPrice" &&
          item.attribute !== "offers.offers.deliveryLeadTime.maxValue"
      ),
    [refinedItems]
  )

  const { refine: clearRefinements } = useClearRefinements()

  const {
    currentRefinement,
    options: sortByOptions,
    refine: sortByRefine,
  } = useSortBy({
    items: sortOptions,
  })

  const isSortByRefined = currentRefinement !== "products"

  const sortByRef = useRef(null)

  const filterBgColor = useColorModeValue("gray.100", "whiteAlpha.200")
  const filterTextColor = useColorModeValue("gray.700", "gray.200")

  const turboOptions = [
    {
      label: "on",
      start: 1,
      end: 3,
    },
    {
      label: "off",
    },
  ]

  const { items: turboItems, refine: turboRefine } = useNumericMenu({
    attribute: "offers.offers.deliveryLeadTime.maxValue",
    items: turboOptions,
  })

  const handleTurboChange = () => {
    const refinedItem = turboItems.find((item) => item.isRefined)
    if (refinedItem?.label === "on") {
      turboRefine(turboItems[1].value)
    } else {
      turboRefine(turboItems[0].value)
    }
  }

  // Change tab index when dynamic filters change
  useEffect(() => {
    if (tabName) {
      const tabIndex = dynamicFilterList.findIndex(
        (filter) => filter.attribute === tabName
      )
      setTabIndex(tabIndex)
    }
  }, [dynamicFilterList, tabName])

  return (
    <>
      <HStack
        mt={isHome ? 0 : 2}
        spacing={2}
        overflowX={"scroll"}
        sx={{
          "&::-webkit-scrollbar": {
            display: "none",
          },
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
      >
        <Box>
          <FormControl
            display={"flex"}
            alignItems={"center"}
          >
            <FormLabel
              htmlFor={"turbo"}
              m={"0"}
              mr={1}
            >
              <TurboBadge />
            </FormLabel>
            <Switch
              id={"turbo"}
              colorScheme={"yellow"}
              onChange={handleTurboChange}
              isChecked={turboItems[0].isRefined}
              size={isHome ? "sm" : "md"}
            />
          </FormControl>
        </Box>
        <Box>
          <Button
            aria-label={"Filters"}
            leftIcon={<VscSettings />}
            size={"xs"}
            p={isHome ? 2 : 4}
            fontWeight={"500"}
            onClick={onOpen}
            bgColor={isHome ? "white" : filterBgColor}
            color={isHome ? "black" : filterTextColor}
          >
            {"Filters"}
          </Button>
        </Box>
        {!isHome && (
          <Fragment>
            <HStack spacing={0}>
              {isSortByRefined && (
                <IconButton
                  aria-label={"Clear"}
                  icon={<IoMdClose />}
                  size={"xs"}
                  colorScheme={"yellow"}
                  borderRadius={"full"}
                  onClick={() => sortByRefine(sortByOptions[0].value)}
                />
              )}
              <Popover
                isLazy
                initialFocusRef={sortByRef}
              >
                {({ onClose }) => (
                  <>
                    <PopoverTrigger>
                      <Button
                        size={"xs"}
                        p={4}
                        fontWeight={"500"}
                        borderColor={
                          isSortByRefined ? "yellow.400" : "transparent"
                        }
                        borderWidth={1}
                      >
                        {"Sort"}
                      </Button>
                    </PopoverTrigger>
                    <Portal>
                      <PopoverContent>
                        <PopoverArrow />
                        <PopoverBody>
                          <SortByFilter onClose={onClose} />
                        </PopoverBody>
                      </PopoverContent>
                    </Portal>
                  </>
                )}
              </Popover>
            </HStack>

            <HStack spacing={0}>
              {refinedCategory.length > 0 && (
                <IconButton
                  aria-label={"Clear"}
                  icon={<IoMdClose />}
                  size={"xs"}
                  colorScheme={"yellow"}
                  borderRadius={"full"}
                  onClick={() => refine(refinedCategory[0].refinements[0])}
                />
              )}
              <Button
                size={"xs"}
                p={4}
                fontWeight={"500"}
                onClick={() => {
                  setTabIndex(1)
                  setTabName("category.lvl0.en_AE")
                  onOpen()
                }}
                borderColor={
                  refinedCategory.length > 0 ? "yellow.400" : "transparent"
                }
                borderWidth={1}
              >
                {refinedCategory.length > 0
                  ? refinedCategory[0].refinements[0].label
                      .split(" > ")
                      .slice(-1)
                  : "Categories"}
              </Button>
            </HStack>

            {refinedDatePublished.length > 0 && (
              <HStack spacing={0}>
                <IconButton
                  aria-label={"Clear"}
                  icon={<IoMdClose />}
                  size={"xs"}
                  colorScheme={"yellow"}
                  borderRadius={"full"}
                  onClick={() => {
                    refinedDatePublished[0].refinements.map((item) => {
                      refine(item)
                    })
                  }}
                />
                <Button
                  size={"xs"}
                  p={4}
                  fontWeight={"500"}
                  onClick={() => {
                    setTabIndex(2)
                    setTabName("datePublished")
                    onOpen()
                  }}
                  borderColor={"yellow.400"}
                  borderWidth={1}
                >
                  {newReleasesItems.find((item) => item.isRefined)?.label ||
                    "New Releases"}
                </Button>
              </HStack>
            )}

            {refinedRatings.length > 0 && (
              <HStack spacing={0}>
                <IconButton
                  aria-label={"Clear"}
                  icon={<IoMdClose />}
                  size={"xs"}
                  colorScheme={"yellow"}
                  borderRadius={"full"}
                  onClick={() => {
                    refinedRatings[0].refinements.map((item) => {
                      refine(item)
                    })
                  }}
                />
                <Button
                  size={"xs"}
                  p={4}
                  fontWeight={"500"}
                  onClick={() => {
                    setTabIndex(3)
                    setTabName("aggregateRating.ratingValue")
                    onOpen()
                  }}
                  borderColor={"yellow.400"}
                  borderWidth={1}
                >
                  {refinedRatings[0]?.refinements[0]?.value ? (
                    <Flex alignItems={"center"}>
                      {starIcons(
                        Number(refinedRatings[0].refinements[0].value)
                      )}
                      <Box
                        as={"span"}
                        ms={1}
                        fontWeight={500}
                      >
                        {"& up"}
                      </Box>
                    </Flex>
                  ) : (
                    "Ratings"
                  )}
                </Button>
              </HStack>
            )}

            {refinedPrice.length > 0 && (
              <HStack spacing={0}>
                <IconButton
                  aria-label={"Clear"}
                  icon={<IoMdClose />}
                  size={"xs"}
                  colorScheme={"yellow"}
                  borderRadius={"full"}
                  onClick={() => {
                    refinedPrice[0].refinements.map((item) => {
                      refine(item)
                    })
                  }}
                />
                <Button
                  size={"xs"}
                  p={4}
                  fontWeight={"500"}
                  onClick={() => {
                    setTabIndex(4)
                    setTabName("offers.lowPrice")
                    onOpen()
                  }}
                  borderColor={"yellow.400"}
                  borderWidth={1}
                >
                  {refinedPrice[0]?.refinements.length > 0
                    ? `${
                        refinedPrice[0].refinements[0].operator === "<="
                          ? `Upto AED ${refinedPrice[0].refinements[0].value}`
                          : `AED ${refinedPrice[0].refinements[0].value}`
                      }${
                        Number(refinedPrice[0]?.refinements[1]?.value) > 0
                          ? ` - AED ${refinedPrice[0].refinements[1].value}`
                          : refinedPrice[0].refinements[0].operator === "<="
                            ? ""
                            : " and above"
                      }`
                    : "Price"}
                </Button>
              </HStack>
            )}

            {otherRefinements.map((refinement, index) => {
              const title = dynamicFilterList.find(
                (item) => item.attribute === refinement.attribute
              )?.title

              const refinementTitle = title ? title : "Features"

              return (
                <Box key={index}>
                  <Button
                    size={"xs"}
                    p={4}
                    fontWeight={"500"}
                    rightIcon={<BiCaretDown />}
                    leftIcon={
                      <Flex
                        borderRadius={"full"}
                        w={5}
                        h={5}
                        bgColor={"yellow.400"}
                        color={"black"}
                        justifyContent={"center"}
                        alignItems={"center"}
                        me={1}
                      >
                        {refinement.attribute === "datePublished"
                          ? 1
                          : refinement.refinements.length}
                      </Flex>
                    }
                    borderColor={"yellow.400"}
                    borderWidth={1}
                    onClick={() => {
                      const filterIndex = dynamicFilterList.findIndex(
                        (item) => item.attribute === refinement.attribute
                      )
                      setTabIndex(filterIndex)
                      setTabName(refinement.attribute)
                      onOpen()
                    }}
                  >
                    {refinementTitle}
                  </Button>
                </Box>
              )
            })}
          </Fragment>
        )}
      </HStack>

      <VirtualSortBy items={sortOptions} />
      <VirtualNumericMenu
        attribute={"datePublished"}
        items={newReleasesOptions()}
      />
      <VirtualRangeInput attribute={"offers.lowPrice"} />
      <VirtualHierarchicalMenu attributes={menuAttributes} />
      {filterList.map((item, index) => {
        if (!item.component && item.attribute) {
          return (
            <VirtualRefinementList
              key={index}
              attribute={item.attribute}
            />
          )
        }
      })}

      <Portal>
        {isOpen && (
          <Box
            onClick={onClose}
            bgColor={"blackAlpha.700"}
            zIndex={100}
            position={"fixed"}
            left={0}
            right={0}
            top={0}
            bottom={0}
            w={"100vw"}
            h={"100vh"}
            overflow={"hidden"}
          />
        )}
        <Slide
          direction={"bottom"}
          in={isOpen}
          style={{ zIndex: 100 }}
        >
          <Box
            borderTopRadius={8}
            bg={useColorModeValue("white", "black")}
            shadow={"lg"}
          >
            <Box
              borderBottomWidth={1}
              px={6}
              py={4}
              fontSize={"xl"}
              fontWeight={500}
            >
              <Flex
                justifyContent={"space-between"}
                alignItems={"center"}
              >
                <Box as={"span"}>{"Filters"}</Box>
                <IconButton
                  aria-label={"close"}
                  icon={<IoMdClose />}
                  bg={"transparent"}
                  onClick={onClose}
                />
              </Flex>
            </Box>
            <Box
              px={2}
              py={0}
              maxH={"60vh"}
              overflow={"hidden"}
            >
              <Tabs
                index={tabIndex}
                onChange={(index) => {
                  setTabIndex(index)
                  setTabName(dynamicFilterList[index].attribute)
                }}
                orientation={"vertical"}
              >
                <Box
                  overflowY={"auto"}
                  minW={"35vw"}
                  maxHeight={"60vh"}
                  py={2}
                  borderRightWidth={1}
                >
                  <TabList borderColor={"transparent"}>
                    {dynamicFilterList.map((filter, index) => {
                      return (
                        <FilterTab
                          key={index}
                          borderLeftWidth={
                            filter.attribute == "sortBy" ||
                            refinedItems.some(
                              (item) => item.attribute === filter.attribute
                            )
                              ? 4
                              : 0
                          }
                        >
                          {filter.title}
                        </FilterTab>
                      )
                    })}
                  </TabList>
                </Box>
                <Box
                  overflowY={"auto"}
                  maxHeight={"60vh"}
                >
                  <TabPanels>
                    {dynamicFilterList.map((filter, index) => {
                      return (
                        <TabPanel
                          key={index}
                          fontSize={"sm"}
                          py={2}
                        >
                          {filter.attribute && !filter.component && (
                            <FilterList attribute={filter.attribute} />
                          )}
                          {filter.component && <>{filter.component}</>}
                        </TabPanel>
                      )
                    })}
                  </TabPanels>
                </Box>
              </Tabs>
            </Box>
            <Box
              px={6}
              py={4}
            >
              <Flex
                justifyContent={"center"}
                w={"full"}
              >
                <ButtonGroup gap={4}>
                  <Button
                    size={"sm"}
                    fontWeight={"500"}
                    onClick={() => {
                      clearRefinements()
                      sortByRefine(sortByOptions[0].value)
                      onClose()
                    }}
                  >
                    {" "}
                    {"Clear Filters"}
                  </Button>
                  <Button
                    size={"sm"}
                    fontWeight={"500"}
                    color={"black"}
                    bgColor={"yellow.400"}
                    _hover={{
                      bgColor: "yellow.500",
                    }}
                    _active={{
                      bgColor: "yellow.500",
                    }}
                    onClick={onClose}
                  >
                    {"Apply Filters"}
                  </Button>
                </ButtonGroup>
              </Flex>
            </Box>
          </Box>
        </Slide>
      </Portal>
    </>
  )
}

export default SearchFilters
